import { observer } from "mobx-react";
import React, { useContext } from "react";
import {
  Paper,
  Stack,
  TextInput,
  Text,
  Group,
  Box,
  Badge,
  CopyButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowsExchange,
  IconCurrencyBitcoin,
  IconCheck,
  IconCopy,
} from "@tabler/icons-react";
import { SatoshiConverterStoreContext } from "../stores/SatoshiConverterStore";
import { t } from "../helpers/translations";

const ConverterWidget: React.FC = observer(() => {
  const store = useContext(SatoshiConverterStoreContext);
  const s = store.strings;
  const code = store.currencyCode.toUpperCase();

  return (
    <Paper
      p="xl"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Stack gap="lg">
        {/* Sats input */}
        <Box>
          <Text size="sm" c="dimmed" mb={4} fw={500}>
            {s.satoshisLabel}
          </Text>
          <TextInput
            placeholder={s.enterSatoshis}
            value={store.satsInput}
            onChange={(e) => store.setSatsInput(e.currentTarget.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            size="lg"
            styles={{
              input: {
                fontFamily: "courier-prime, monospace",
                fontSize: "20px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                color: "white",
                textAlign: "right",
              },
            }}
            rightSection={
              <Text size="sm" c="dimmed" mr="xs">
                sats
              </Text>
            }
            rightSectionWidth={50}
          />
        </Box>

        {/* Exchange arrows */}
        <Group justify="center">
          <IconArrowsExchange
            size={28}
            color="rgba(255,255,255,0.4)"
            style={{ transform: "rotate(90deg)" }}
          />
        </Group>

        {/* Fiat input */}
        <Box>
          <Text size="sm" c="dimmed" mb={4} fw={500}>
            {store.currencyName} ({code})
          </Text>
          <TextInput
            placeholder={t(s.enterAmountIn, { code })}
            value={store.fiatInput}
            onChange={(e) => store.setFiatInput(e.currentTarget.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            size="lg"
            styles={{
              input: {
                fontFamily: "courier-prime, monospace",
                fontSize: "20px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                color: "white",
                textAlign: "right",
              },
            }}
            rightSection={
              <Text size="sm" c="dimmed" mr="xs">
                {store.currencySymbol}
              </Text>
            }
            rightSectionWidth={50}
          />
        </Box>

        {/* Conversion result with copy button */}
        {store.satsInput && parseInt(store.satsInput, 10) > 0 && (
          <Paper
            p="md"
            radius="sm"
            style={{
              backgroundColor: "rgba(255, 165, 0, 0.08)",
              border: "1px solid rgba(255, 165, 0, 0.2)",
            }}
          >
            <CopyButton
              value={
                store.lastEdited === "fiat" ? store.satsInput : store.fiatInput
              }
            >
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied!" : "Copy to clipboard"}
                  withArrow
                  position="top"
                >
                  <Box
                    onClick={copy}
                    style={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      transition: "background-color 150ms ease",
                      backgroundColor: copied
                        ? "rgba(32, 201, 151, 0.1)"
                        : "transparent",
                    }}
                  >
                    <Group justify="center" gap={6} align="baseline">
                      <Text
                        size="xs"
                        fw={400}
                        style={{
                          fontFamily: "courier-prime, monospace",
                          color: "rgba(255, 255, 255, 0.35)",
                          transition: "color 150ms ease",
                        }}
                      >
                        {store.lastEdited === "fiat"
                          ? `${store.fiatInput} ${code} =`
                          : `${store.formattedSats} sats =`}
                      </Text>
                      <Text
                        size="xl"
                        fw={700}
                        style={{
                          fontFamily: "courier-prime, monospace",
                          color: copied ? "#20c997" : "#f7931a",
                          fontSize: "24px",
                          transition: "color 150ms ease",
                        }}
                      >
                        {store.lastEdited === "fiat"
                          ? `${store.formattedSats} sats`
                          : `${store.fiatInput} ${code}`}
                      </Text>
                      <ActionIcon
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "orange"}
                        size="lg"
                        radius="xl"
                        style={{
                          transition: "all 150ms ease",
                        }}
                      >
                        {copied ? (
                          <IconCheck size={18} />
                        ) : (
                          <IconCopy size={18} />
                        )}
                      </ActionIcon>
                    </Group>
                  </Box>
                </Tooltip>
              )}
            </CopyButton>

            {/* BTC equivalent — secondary info with copy */}
            <Group justify="center" gap={6} mt="xs">
              <IconCurrencyBitcoin size={16} color="rgba(247, 147, 26, 0.6)" />
              <Text
                size="xs"
                c="dimmed"
                style={{ fontFamily: "courier-prime, monospace" }}
              >
                {store.btcAmount} BTC
              </Text>
              <CopyButton value={store.btcAmount}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? "Copied!" : "Copy BTC"}
                    withArrow
                    position="right"
                  >
                    <ActionIcon
                      variant="transparent"
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      onClick={copy}
                    >
                      {copied ? (
                        <IconCheck size={12} />
                      ) : (
                        <IconCopy size={12} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Paper>
        )}

        {/* Live rate badge */}
        <Group justify="center">
          <Badge
            variant="dot"
            color="teal"
            size="sm"
            style={{ backgroundColor: "rgba(0,0,0,0.3)" }}
          >
            1 BTC ={" "}
            {store.btcPriceInFiat.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}{" "}
            {store.currencyCode.toUpperCase()}
          </Badge>
        </Group>
      </Stack>
    </Paper>
  );
});

export default ConverterWidget;
